"use client";

import { claimAudioPreview, releaseAudioPreview } from "@/lib/audioPreviewController";
import { cn } from "@/lib/utils";
import { Pause, Play, Volume2 } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

export function AccessibleAudioPlayer({
  src,
  title,
  compact = false,
  allowPlaybackRate = false,
  onFirstPlay,
  className,
}: {
  src: string;
  title: string;
  compact?: boolean;
  allowPlaybackRate?: boolean;
  onFirstPlay?: () => void;
  className?: string;
}) {
  const id = useId();
  const audioRef = useRef<HTMLAudioElement>(null);
  const firstPlayRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => {
      claimAudioPreview(id, audio, () => setPlaying(false));
      setPlaying(true);
      if (!firstPlayRef.current) {
        firstPlayRef.current = true;
        onFirstPlay?.();
      }
    };
    const onPause = () => setPlaying(false);
    const onTime = () => setCurrentTime(audio.currentTime);
    const onMetadata = () => {
      setDuration(audio.duration || 0);
      setError(null);
    };
    const onEnded = () => {
      setPlaying(false);
      releaseAudioPreview(id);
    };
    const onError = () => setError("This audio is unavailable. Try loading it again.");
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMetadata);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      releaseAudioPreview(id);
      audio.pause();
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMetadata);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [id, onFirstPlay, src]);

  const toggle = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);
    try {
      if (audio.paused) await audio.play();
      else audio.pause();
    } catch {
      setError("Playback could not start. Check your connection and try again.");
    }
  }, []);

  const seek = useCallback((value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  }, []);

  const changeRate = useCallback((value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = value;
    setPlaybackRate(value);
  }, []);

  return (
    <div className={cn("audio-player rounded-xl border border-border bg-card p-3", className)}>
      <audio ref={audioRef} src={src} preload="metadata" aria-label={title} />
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={() => void toggle()}
          aria-label={playing ? `Pause ${title}` : `Play ${title}`}
          aria-pressed={playing}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-foreground text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4 translate-x-px" fill="currentColor" />}
        </button>
        <div className="min-w-0 flex-1">
          {!compact ? (
            <div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-foreground">
              <Volume2 className="h-3.5 w-3.5 text-success" aria-hidden />
              <span className="truncate">{title}</span>
            </div>
          ) : null}
          <label className="sr-only" htmlFor={`${id}-seek`}>Seek through {title}</label>
          <input
            id={`${id}-seek`}
            type="range"
            min={0}
            max={duration || 1}
            step={0.1}
            value={Math.min(currentTime, duration || 1)}
            onChange={(event) => seek(Number(event.target.value))}
            aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
            className="h-11 w-full cursor-pointer accent-success focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success"
          />
          <div className="flex items-center justify-between text-[11px] tabular-nums text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        {allowPlaybackRate ? (
          <label className="shrink-0 text-xs text-muted-foreground">
            <span className="sr-only">Playback speed</span>
            <select
              value={playbackRate}
              onChange={(event) => changeRate(Number(event.target.value))}
              className="h-11 rounded-lg border border-border bg-card px-2 text-xs font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success"
            >
              <option value={0.75}>0.75×</option>
              <option value={1}>1×</option>
              <option value={1.25}>1.25×</option>
              <option value={1.5}>1.5×</option>
            </select>
          </label>
        ) : null}
      </div>
      {error ? <p role="alert" className="mt-2 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
